import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  Divider,
  Fade,
  Chip
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import { Fileuploadrequestcall } from "../../request/Fileuploadrequest";
import {
  CLIENT_ANALYZE_ASSET_IMAGE,
  FETCH_CLIENT_ASSET_CATEGORIES,
  FETCH_CLIENT_ASSET_SUB_CATEGORIES,
  FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES,
  FETCH_COMPANIES_2,
  SAVE_CLIENT_FIXED_ASSETS,
  SAVE_CLIENT_FIXED_ASSET_PICTURES
} from "../../constant/Apipath";
import { showErrorToast, showSuccessToast } from "../../toast/toast";
import { getFallbackFormFields } from "./constant/formFields";
import DynamicFormField from "./components/DynamicFormField";

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      color: "#94a3b8",
      mb: 2,
      mt: 1
    }}>
    {children}
  </Typography>
);

const AddFixedAssetModal = ({ open, onClose, onRefresh }) => {
  const { loginData, selectedClientData } = useSelector((state) => state?.main || {});
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);

  const [dropdownOptions, setDropdownOptions] = useState({});
  const [dropdownLoading, setDropdownLoading] = useState({});
  const [dropdownLoadingMore, setDropdownLoadingMore] = useState({});
  const [dropdownPage, setDropdownPage] = useState({});
  const [dropdownHasMore, setDropdownHasMore] = useState({});
  const [vendorSearchText, setVendorSearchText] = useState("");
  const [vendorPage, setVendorPage] = useState(1);
  const [vendorHasMore, setVendorHasMore] = useState(true);
  const [vendorTotalRecords, setVendorTotalRecords] = useState(0);
  const [vendorLoadingMore, setVendorLoadingMore] = useState(false);
  const [images, setImages] = useState([]);

  // Lens states
  const lensInputRef = useRef(null);
  const [lensState, setLensState] = useState("idle"); // "idle" | "analyzing" | "result"
  const [lensResult, setLensResult] = useState(null);
  const [lensSelectedFiles, setLensSelectedFiles] = useState([]);
  const [lensPreviews, setLensPreviews] = useState([]);
  const [lensMatchedSystemIds, setLensMatchedSystemIds] = useState(null);

  const accent = selectedClientData?.AccentColor || "#6366f1";

  const handleLensClick = () => {
    if (lensInputRef.current) {
      lensInputRef.current.click();
    }
  };

  const handleLensFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const nonImages = files.filter((file) => !file.type.startsWith("image/"));
    if (nonImages.length > 0) {
      showErrorToast("Only image files are allowed");
      return;
    }

    const previews = files.map((file) => URL.createObjectURL(file));
    setLensPreviews(previews);
    setLensSelectedFiles(files);
    await analyzeImages(files);
  };

  const analyzeImages = async (files) => {
    setLensState("analyzing");
    setLensResult(null);
    try {
      const formDataPayload = new FormData();
      files.forEach((file) => {
        // formDataPayload.append("files", file);
        formDataPayload.append("images", file);
      });
      if (selectedClientData?.ClientId) {
        formDataPayload.append("ClientId", selectedClientData.ClientId);
      }

      const response = await Fileuploadrequestcall(
        CLIENT_ANALYZE_ASSET_IMAGE,
        formDataPayload,
        loginData?.accessToken
      );

      if (response?.status === 200 || response?.status === 201) {
        const resData = response?.data;
        setLensResult(resData);
        setLensState("result");
      } else {
        setLensResult({
          status: false,
          message: response?.data?.message || "We couldn't identify a product from this image. Please try with a different photo or fill the form manually."
        });
        setLensState("result");
      }
    } catch (err) {
      setLensResult({
        status: false,
        message: err.message || "An error occurred during analysis."
      });
      setLensState("result");
    }
  };

  const handleLensAccept = () => {
    if (lensResult && lensResult.status && lensResult.data) {
      const data = lensResult.data;
      const product = data.productDetails || {};
      const system = data.matchedSystemIds || {};

      setLensMatchedSystemIds(system);

      const hasField = (fieldKey) =>
        formFields.some((f) => f.fieldKey === fieldKey && f.isVisible !== false);

      // Prepopulate the dropdown lists so Autocomplete components render their labels correctly
      setDropdownOptions((prev) => ({
        ...prev,
        CATEGORY: hasField("CATEGORY") && system.categoryName
          ? [{ id: system.categoryId ?? 0, label: system.categoryName }]
          : (prev.CATEGORY || []),
        SUB_CATEGORY: hasField("SUB_CATEGORY") && system.subCategoryName
          ? [{ id: system.subCategoryId ?? 0, label: system.subCategoryName }]
          : (prev.SUB_CATEGORY || []),
        VENDOR: hasField("VENDOR") && (system.vendorName && system.vendorId !== 0)
          ? [{ id: system.vendorId, label: system.vendorName }]
          : (prev.VENDOR || [])
      }));

      setFormData((prev) => ({
        ...prev,
        ASSET_NAME: hasField("ASSET_NAME") ? (product.productName || prev.ASSET_NAME || "") : prev.ASSET_NAME,
        PRODUCT_NAME: hasField("PRODUCT_NAME") ? (product.brand || prev.PRODUCT_NAME || "") : prev.PRODUCT_NAME,
        MODEL: hasField("MODEL") ? (product.modelNumber || prev.MODEL || "") : prev.MODEL,
        SERIAL_NUMBER: hasField("SERIAL_NUMBER") ? (product.serialNumber || prev.SERIAL_NUMBER || "") : prev.SERIAL_NUMBER,
        BARCODE: hasField("BARCODE") ? (product.barcode || prev.BARCODE || "") : prev.BARCODE,
        PRICE: hasField("PRICE") ? (product.estimatedPrice || prev.PRICE || "") : prev.PRICE,
        COMMENTS: hasField("COMMENTS") ? (product.description || prev.COMMENTS || "") : prev.COMMENTS,
        CATEGORY: hasField("CATEGORY") && system.categoryName
          ? { id: system.categoryId ?? 0, label: system.categoryName }
          : prev.CATEGORY,
        SUB_CATEGORY: hasField("SUB_CATEGORY") && system.subCategoryName
          ? { id: system.subCategoryId ?? 0, label: system.subCategoryName }
          : prev.SUB_CATEGORY,
        VENDOR: hasField("VENDOR") && (system.vendorName && system.vendorId !== 0)
          ? { id: system.vendorId, label: system.vendorName }
          : prev.VENDOR
      }));

      if (lensSelectedFiles.length > 0 && (hasField("ADD_IMAGES") || hasField("UPLOAD_PICTURES"))) {
        const mapped = lensSelectedFiles.map((file) => ({
          file,
          preview: URL.createObjectURL(file)
        }));
        setImages((prev) => [...prev, ...mapped]);
      }
    }
    setLensState("idle");
  };

  const handleLensReject = () => {
    onClose();
  };

  const fetchFormFields = useCallback(async () => {
    setLoadingFields(true);
    try {
      const res = await Postrequestcall(
        "Client/FetchClientFormFieldSettings2",
        {
          PageNr: 1,
          NrOfRecPerPage: 20,
          FullSearch: "",
          UserId: loginData?.userInformation?.UserId || 1,
          IncludeRecordNr: "true",
          TypeOfObjectReturned: "",
          SearchList: [
            {
              ClientId: selectedClientData?.ClientId,
              appId: 21,
              AppFormKey: "ADD_ASSET",
              // formName: "AddAsset",
              isVisible: true
            }
          ],
          SortList: [{}]
        },
        loginData?.accessToken
      );

      const fields =
        res?.status === 200 && res?.data?.data?.length > 0
          ? res.data.data
          : getFallbackFormFields("AddAsset");

      setFormFields(fields);

      const initialData = {};
      fields.forEach((f) => {
        initialData[f.fieldKey] = "";
      });
      setFormData(initialData);

      if (fields.find((f) => f.fieldKey === "CATEGORY"))
        fetchDropdownData(FETCH_CLIENT_ASSET_CATEGORIES, "CATEGORY");
      if (fields.find((f) => f.fieldKey === "STATUS"))
        fetchDropdownData(FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES, "STATUS");
    } catch (e) {
      const fallback = getFallbackFormFields("AddAsset");
      setFormFields(fallback);
    } finally {
      setLoadingFields(false);
    }
  }, [open, selectedClientData, loginData]);

  useEffect(() => {
    if (open) {
      fetchFormFields();
      setImages([]);
      setDropdownOptions({});
      setDropdownLoadingMore({});
      setDropdownPage({});
      setDropdownHasMore({});
      setVendorSearchText("");
      setVendorPage(1);
      setVendorHasMore(true);
      setVendorTotalRecords(0);
      setVendorLoadingMore(false);
      setDropdownOptions((prev) => ({ ...prev, VENDOR: [] }));
      setLensState("idle");
      setLensResult(null);
      setLensSelectedFiles([]);
      setLensPreviews([]);
      setLensMatchedSystemIds(null);
    }
  }, [open]);

  const fetchDropdownData = async (url, fieldKey, extraSearch = {}, page = 1, append = false) => {
    const pageSize = 20;
    if (append) {
      setDropdownLoadingMore((prev) => ({ ...prev, [fieldKey]: true }));
    }
    setDropdownLoading((prev) => ({ ...prev, [fieldKey]: true }));
    try {
      const payload = {
        PageNr: page,
        NrOfRecPerPage: pageSize,
        FullSearch: "",
        SearchList: [{ ClientId: selectedClientData?.ClientId, ...extraSearch }],
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true
      };
      const res = await Postrequestcall(url, payload, loginData?.accessToken);
      if (res?.status === 200) {
        const items = res.data.data || [];
        const data = items.map((item) => ({
          id: item.id,
          label: item.name
        }));
        const totalRecords = Number(res?.data?.nrOfRecords ?? res?.data?.RecordNr ?? 0);
        setDropdownOptions((prev) => ({
          ...prev,
          [fieldKey]: append ? [...(prev[fieldKey] || []), ...data] : data
        }));
        setDropdownPage((prev) => ({ ...prev, [fieldKey]: page }));
        setDropdownHasMore((prev) => ({
          ...prev,
          [fieldKey]: totalRecords > 0 ? page * pageSize < totalRecords : items.length === pageSize
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDropdownLoading((prev) => ({ ...prev, [fieldKey]: false }));
      if (append) {
        setDropdownLoadingMore((prev) => ({ ...prev, [fieldKey]: false }));
      }
    }
  };

  const fetchVendors = async (searchText = "", page = 1, append = false) => {
    const pageSize = 20;
    if (append) {
      setVendorLoadingMore(true);
    }
    setDropdownLoading((prev) => ({ ...prev, VENDOR: true }));
    try {
      const payload = {
        UserId: loginData?.userInformation?.UserId,
        SearchString: searchText,
        PageNumber: page,
        PageSize: pageSize,
        Manufacturer: true,
        Id: 0
      };
      const res = await Postrequestcall(FETCH_COMPANIES_2, payload, loginData?.accessToken);
      if (res?.status === 201) {
        const vendors = res.data.data || [];
        const mappedVendors = vendors.map((v) => ({ id: v.id, label: v.name }));
        const totalRecords = Number(res?.data?.nrOfRecords ?? res?.data?.RecordNr ?? 0);
        setDropdownOptions((prev) => ({
          ...prev,
          VENDOR: append ? [...(prev.VENDOR || []), ...mappedVendors] : mappedVendors
        }));
        setVendorPage(page);
        setVendorTotalRecords(totalRecords);
        setVendorHasMore(
          totalRecords > 0 ? page * pageSize < totalRecords : vendors.length === pageSize
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDropdownLoading((prev) => ({ ...prev, VENDOR: false }));
      if (append) {
        setVendorLoadingMore(false);
      }
    }
  };

  const handleFieldChange = (fieldKey, value) => {
    const dropdownFieldKeys = ["CATEGORY", "SUB_CATEGORY", "STATUS", "VENDOR"];

    if (dropdownFieldKeys.includes(fieldKey) && typeof value === "string") {
      return;
    }

    if (fieldKey === "SUB_CATEGORY") {
      const categoryFieldExists = formFields.some((field) => field.fieldKey === "CATEGORY");
      const hasCategory = !!(
        formData.CATEGORY &&
        ((typeof formData.CATEGORY === "object" && formData.CATEGORY.id) ||
          (typeof formData.CATEGORY === "string" && formData.CATEGORY.trim() !== "") ||
          typeof formData.CATEGORY === "number")
      );

      if (categoryFieldExists && !hasCategory) {
        showErrorToast("Please select Category first");
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [fieldKey]: value }));

    if (fieldKey === "CATEGORY" && value) {
      const selectedCat = dropdownOptions.CATEGORY?.find((c) => {
        if (value && typeof value === "object") {
          return c.id === value.id || c.label === value.label;
        }
        return c.label === value || c.id === value;
      });
      if (selectedCat) {
        setDropdownOptions((prev) => ({ ...prev, SUB_CATEGORY: [] }));
        setDropdownPage((prev) => ({ ...prev, SUB_CATEGORY: 1 }));
        setDropdownHasMore((prev) => ({ ...prev, SUB_CATEGORY: true }));
        fetchDropdownData(
          FETCH_CLIENT_ASSET_SUB_CATEGORIES,
          "SUB_CATEGORY",
          {
            AssetCategoryId: selectedCat.id
          },
          1,
          false
        );
      }
    }
  };

  const handleDropdownLoadMore = (fieldKey) => {
    if (dropdownLoading[fieldKey] || !dropdownHasMore[fieldKey]) return;

    const api =
      fieldKey === "CATEGORY"
        ? FETCH_CLIENT_ASSET_CATEGORIES
        : fieldKey === "SUB_CATEGORY"
          ? FETCH_CLIENT_ASSET_SUB_CATEGORIES
          : fieldKey === "STATUS"
            ? FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES
            : null;

    if (!api) return;

    const nextPage = (dropdownPage[fieldKey] || 1) + 1;
    const extraSearch =
      fieldKey === "SUB_CATEGORY"
        ? {
            AssetCategoryId: dropdownOptions.CATEGORY?.find(
              (c) => c.label === formData.CATEGORY || c.id === formData.CATEGORY
            )?.id
          }
        : {};

    fetchDropdownData(api, fieldKey, extraSearch, nextPage, true);
  };

  const handleVendorLoadMore = () => {
    if (dropdownLoading.VENDOR || !vendorHasMore) return;
    fetchVendors(vendorSearchText, vendorPage + 1, true);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setImages((prev) => [...prev, ...mapped]);
  };

  const validateForm = () => {
    for (const field of formFields) {
      if (
        field.isVisible &&
        field.isRequired &&
        field.fieldKey !== "ADD_IMAGES" &&
        !formData[field.fieldKey]
      ) {
        showErrorToast(`${field.fieldName} is required`);
        return false;
      }
      if (
        field.fieldKey === "ADD_IMAGES" &&
        field.isVisible &&
        field.isRequired &&
        images.length === 0
      ) {
        showErrorToast(`${field.fieldName} is required`);
        return false;
      }
    }
    return true;
  };

  const resolveDropdownId = (value) => {
    if (value && typeof value === "object" && value.id !== undefined) {
      return value.id;
    }

    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    return 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const hasField = (fieldKey) =>
        formFields.some((f) => f.fieldKey === fieldKey && f.isVisible !== false);

      let finalCategoryId = hasField("CATEGORY") ? resolveDropdownId(formData.CATEGORY) : 0;
      let finalSubCategoryId = hasField("SUB_CATEGORY") ? resolveDropdownId(formData.SUB_CATEGORY) : 0;

      if (lensMatchedSystemIds) {
        const catName = lensMatchedSystemIds.categoryName;
        const subCatName = lensMatchedSystemIds.subCategoryName;

        // If category ID is 0, categoryName is provided, and CATEGORY field is configured
        if (hasField("CATEGORY") && finalCategoryId === 0 && catName) {
          try {
            const catPayload = {
              ReturnRecordId: true,
              ReturnRecordError: true,
              UserId: loginData?.userInformation?.UserId,
              SaveList: [
                {
                  Id: 0,
                  Name: catName,
                  Description: `${catName} created via BI Lens`,
                  ClientId: selectedClientData?.ClientId,
                  ModifiedBy: loginData?.userInformation?.UserId
                }
              ]
            };
            const catRes = await Postrequestcall(
              "Client/SaveClientAssetCategories",
              catPayload,
              loginData?.accessToken
            );
            if (catRes?.status === 200 || catRes?.status === 201) {
              const newCatId = catRes?.data?.data?.[0]?.objData?.id || catRes?.data?.RecordId;
              if (newCatId) {
                finalCategoryId = newCatId;
              }
            }
          } catch (catErr) {
            console.error("Failed to create category via BI Lens:", catErr);
          }
        }

        // If subcategory ID is 0, subcategoryName is provided, category ID is valid, and SUB_CATEGORY field is configured
        if (hasField("SUB_CATEGORY") && finalSubCategoryId === 0 && subCatName && finalCategoryId !== 0) {
          try {
            const subCatPayload = {
              ReturnRecordId: true,
              ReturnRecordError: true,
              UserId: loginData?.userInformation?.UserId,
              SaveList: [
                {
                  Id: 0,
                  Name: subCatName,
                  Description: `${subCatName} created via BI Lens`,
                  AssetCategoryId: finalCategoryId,
                  ClientId: selectedClientData?.ClientId,
                  ModifiedBy: loginData?.userInformation?.UserId
                }
              ]
            };
            const subCatRes = await Postrequestcall(
              "Client/SaveClientAssetSubCategories",
              subCatPayload,
              loginData?.accessToken
            );
            if (subCatRes?.status === 200 || subCatRes?.status === 201) {
              const newSubCatId = subCatRes?.data?.data?.[0]?.objData?.id || subCatRes?.data?.RecordId;
              if (newSubCatId) {
                finalSubCategoryId = newSubCatId;
              }
            }
          } catch (subCatErr) {
            console.error("Failed to create subcategory via BI Lens:", subCatErr);
          }
        }
      }

      const saveData = {
        Id: 0,
        AssetCode: hasField("ASSET_CODE") ? (formData.ASSET_CODE || "") : "",
        Asset: hasField("ASSET_NAME") ? (formData.ASSET_NAME || "") : "",
        AssetStatusId: hasField("STATUS") ? resolveDropdownId(formData.STATUS) : 0,
        AssetCategoryId: hasField("CATEGORY") ? finalCategoryId : 0,
        AssetSubCategoryId: hasField("SUB_CATEGORY") ? finalSubCategoryId : 0,
        BarCode: hasField("BARCODE") ? (formData.BARCODE || "") : "",
        SerialNo: hasField("SERIAL_NUMBER") ? (formData.SERIAL_NUMBER || "") : "",
        Make: hasField("PRODUCT_NAME") ? (formData.PRODUCT_NAME || "") : "",
        Model: hasField("MODEL") ? (formData.MODEL || "") : "",
        VendorId: hasField("VENDOR") ? resolveDropdownId(formData.VENDOR) : 0,
        PurchasedDate:
          hasField("PURCHASE_DATE") && formData.PURCHASE_DATE
            ? new Date(formData.PURCHASE_DATE).toISOString()
            : null,
        Price: hasField("PRICE") ? (parseFloat(formData.PRICE) || 0) : 0,
        WarrantyExpireDate:
          hasField("WARRANTY_EXPIRY_DATE") && formData.WARRANTY_EXPIRY_DATE
            ? new Date(formData.WARRANTY_EXPIRY_DATE).toISOString()
            : null,
        Comments: hasField("COMMENTS") ? (formData.COMMENTS || "") : "",
        ClientId: selectedClientData?.ClientId,
        ModifiedBy: loginData?.userInformation?.UserId
      };

      const payload = {
        ReturnRecordId: true,
        ReturnRecordError: true,
        SaveList: [saveData],
        UserId: loginData?.userInformation?.UserId
      };
      const res = await Postrequestcall(SAVE_CLIENT_FIXED_ASSETS, payload, loginData?.accessToken);

      if (res?.status === 200 || res?.status === 201) {
        const assetId = res?.data?.data?.[0]?.objData?.id || res?.data?.RecordId;
        const canUploadImages = hasField("ADD_IMAGES") || hasField("UPLOAD_PICTURES");
        if (images.length > 0 && assetId && canUploadImages) {
          for (const img of images) {
            const imgData = new FormData();
            imgData.append("Id", 0);
            imgData.append("AssetId", assetId);
            imgData.append("PictureUrl", img.file);
            imgData.append("PictureTypeId", 1);
            imgData.append("ClientId", selectedClientData?.ClientId);
            imgData.append("ModifiedBy", loginData?.userInformation?.UserId);
            imgData.append("UserId", loginData?.userInformation?.UserId);
            await Postrequestcall(
              SAVE_CLIENT_FIXED_ASSET_PICTURES,
              imgData,
              loginData?.accessToken,
              true
            );
          }
        }
        showSuccessToast("Asset created successfully");
        onRefresh();
        onClose();
      } else {
        throw new Error(res?.data?.message || "Failed to save asset");
      }
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  const visibleFields = formFields
    .filter((f) => f.isVisible !== false)
    .sort((a, b) => (parseInt(a.displayOrder) || 0) - (parseInt(b.displayOrder) || 0));

  const imageField = visibleFields.find((f) => f.fieldKey === "ADD_IMAGES");
  const regularFields = visibleFields.filter((f) => f.fieldKey !== "ADD_IMAGES");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 250 }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
          overflow: "hidden"
        }
      }}>
      {/* Header */}
      <DialogTitle
        sx={{
          p: 0,
          background: `linear-gradient(135deg, ${accent}18 0%, ${accent}06 100%)`,
          borderBottom: "1px solid #f1f5f9"
        }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                bgcolor: accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 12px ${accent}44`
              }}>
              <InventoryIcon sx={{ color: "#fff", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", lineHeight: 1.2 }}>
                Add Fixed Asset
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Fill in the details below to register a new asset
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <input
              type="file"
              multiple
              accept="image/*"
              ref={lensInputRef}
              onChange={handleLensFileChange}
              style={{ display: "none" }}
            />
            <IconButton
              onClick={handleLensClick}
              size="small"
              title="BI Lens"
              sx={{
                position: "relative",
                color: "#fff",
                background: `linear-gradient(135deg, ${accent} 0%, #a855f7 100%)`,
                borderRadius: "8px",
                width: 32,
                height: 32,
                boxShadow: `0 2px 8px ${accent}44`,
                "&:hover": {
                  background: `linear-gradient(135deg, #a855f7 0%, ${accent} 100%)`,
                  boxShadow: `0 4px 12px ${accent}66`
                }
              }}>
              <CloudUploadIcon fontSize="small" sx={{ fontSize: 16 }} />
              <AutoAwesomeIcon 
                sx={{ 
                  position: "absolute", 
                  top: -2, 
                  right: -2, 
                  fontSize: 10, 
                  color: "#ffd700",
                  filter: "drop-shadow(0 0 2px rgba(0,0,0,0.5))"
                }} 
              />
            </IconButton>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: "#64748b",
                bgcolor: "#f1f5f9",
                borderRadius: "8px",
                "&:hover": { bgcolor: "#e2e8f0", color: "#1e293b" }
              }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          p: 0,
          bgcolor: "#f8fafc",
          overflowY: "auto",
          maxHeight: "72vh"
        }}>
        {lensState === "analyzing" ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 8,
              py: 12,
              gap: 3,
              textAlign: "center"
            }}>
            <CircularProgress sx={{ color: accent }} size={50} thickness={4} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}>
                Analyzing Asset Image
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", maxWidth: 400 }}>
                Please wait while our BI Lens identifies the product details and properties from your uploaded images.
              </Typography>
            </Box>
          </Box>
        ) : lensState === "result" ? (
          <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}>
            {lensResult?.status ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      bgcolor: "#ecfdf5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                    <CheckIcon sx={{ color: "#10b981" }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1.1rem" }}>
                      Image Analyzed Successfully
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      We found the following asset details from the image:
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                {lensPreviews && lensPreviews.length > 0 && (
                  <Box sx={{ display: "flex", position: "relative", height: 95, mt: 1.5, mb: 1, pl: 0.5 }}>
                    {lensPreviews.map((preview, idx) => (
                      <Box
                        key={idx}
                        component="img"
                        src={preview}
                        alt={`Analyzed image ${idx + 1}`}
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "8px",
                          objectFit: "cover",
                          border: "2px solid #fff",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)",
                          position: "absolute",
                          left: idx * 45,
                          zIndex: idx,
                          transform: `rotate(${idx % 2 === 0 ? idx * 2.5 : -idx * 2.5}deg)`,
                          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "scale(1.2) rotate(0deg) translateY(-8px)",
                            zIndex: 99,
                            boxShadow: "0 12px 20px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)"
                          }
                        }}
                      />
                    ))}
                  </Box>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: accent, mb: 2, textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                        Product Details
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Product Name:</Typography>
                          <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>{lensResult.data?.productDetails?.productName || "N/A"}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Brand (Make):</Typography>
                          <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>{lensResult.data?.productDetails?.brand || "N/A"}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Model Number:</Typography>
                          <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>{lensResult.data?.productDetails?.modelNumber || "N/A"}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Serial Number:</Typography>
                          <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>{lensResult.data?.productDetails?.serialNumber || "N/A"}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Barcode:</Typography>
                          <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>{lensResult.data?.productDetails?.barcode || "N/A"}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Estimated Price:</Typography>
                          <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>
                            {lensResult.data?.productDetails?.estimatedPrice != null ? `$${lensResult.data.productDetails.estimatedPrice}` : "N/A"}
                          </Typography>
                        </Box>
                        {lensResult.data?.productDetails?.description && (
                          <Box sx={{ mt: 1, pt: 1.5, borderTop: "1px dashed #e2e8f0" }}>
                            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500, mb: 0.5 }}>Description:</Typography>
                            <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 500, lineHeight: 1.4 }}>
                              {lensResult.data.productDetails.description}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <Box sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: accent, mb: 2, textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                          System Mapping
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Category:</Typography>
                            <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>{lensResult.data?.matchedSystemIds?.categoryName || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Sub-Category:</Typography>
                            <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>{lensResult.data?.matchedSystemIds?.subCategoryName || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Vendor:</Typography>
                            <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>{lensResult.data?.matchedSystemIds?.vendorName || "N/A"}</Typography>
                          </Box>
                        </Box>
                      </Box>

                      {lensResult.data?.locationDetails && (
                        <Box sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: accent, mb: 2, textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                            Location Details
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Place Name:</Typography>
                              <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>{lensResult.data.locationDetails.placeName || "N/A"}</Typography>
                            </Box>
                            {(lensResult.data.locationDetails.latitude != null || lensResult.data.locationDetails.longitude != null) && (
                              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>Coordinates:</Typography>
                                <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>
                                  {lensResult.data.locationDetails.latitude != null ? lensResult.data.locationDetails.latitude.toFixed(6) : "0.00"}, {lensResult.data.locationDetails.longitude != null ? lensResult.data.locationDetails.longitude.toFixed(6) : "0.00"}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}

                      {lensResult.data?.webReferences && lensResult.data.webReferences.length > 0 && (
                        <Box sx={{ bgcolor: "#fff", p: 2.5, borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: accent, mb: 2, textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                            Web References
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {lensResult.data.webReferences.map((ref, idx) => (
                              <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>•</Typography>
                                <a
                                  href={ref.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: accent, textDecoration: "none", fontWeight: 600, fontSize: "0.875rem" }}
                                >
                                  {ref.title || ref.url}
                                </a>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  py: 6,
                  gap: 3,
                  textAlign: "center"
                }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "#fef2f2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                  <CloudUploadIcon sx={{ color: "#ef4444", fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}>
                    Identification Failed
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", maxWidth: 450 }}>
                    {lensResult?.message || "We couldn't identify a product from this image. Please try with a different photo or fill the form manually."}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        ) : loadingFields ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 8,
              gap: 2
            }}>
            <CircularProgress sx={{ color: accent }} size={36} thickness={3} />
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              Loading form fields…
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Image Upload section */}
            {imageField && (
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  p: 3
                }}>
                <SectionLabel>Photos</SectionLabel>

                <DynamicFormField
                  field={imageField}
                  value={formData[imageField.fieldKey]}
                  onChange={(val) => handleFieldChange(imageField.fieldKey, val)}
                  accentColor={accent}
                  uploadedImages={images}
                  onImageAdd={handleImageChange}
                  onImageRemove={(i) => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                />
              </Box>
            )}

            {/* Main fields */}
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                p: 3,
                mb: 2
              }}>
              <SectionLabel>Asset Details</SectionLabel>
              <Grid container spacing={2.5}>
                {regularFields.map((field) => (
                  <Grid
                    size={{
                      xs: 12,
                      sm: field.fieldType === "MultilineText" ? 12 : 6,
                      md: field.fieldType === "MultilineText" ? 12 : 4
                    }}
                    key={field.id || field.fieldKey}>
                    <DynamicFormField
                      field={field}
                      value={formData[field.fieldKey]}
                      onChange={(val) => handleFieldChange(field.fieldKey, val)}
                      options={dropdownOptions[field.fieldKey] || []}
                      loading={false}
                      loadingMore={false}
                      onSearch={
                        field.fieldKey === "VENDOR"
                          ? (searchText) => {
                              setVendorSearchText(searchText);
                              setVendorPage(1);
                              setVendorHasMore(true);
                              setVendorTotalRecords(0);
                              setVendorLoadingMore(false);
                              setDropdownOptions((prev) => ({
                                ...prev,
                                VENDOR: []
                              }));
                              fetchVendors(searchText, 1, false);
                            }
                          : undefined
                      }
                      onLoadMore={
                        field.fieldKey === "VENDOR"
                          ? handleVendorLoadMore
                          : ["CATEGORY", "SUB_CATEGORY", "STATUS"].includes(field.fieldKey)
                            ? () => handleDropdownLoadMore(field.fieldKey)
                            : undefined
                      }
                      hasMore={
                        field.fieldKey === "VENDOR"
                          ? vendorHasMore
                          : ["CATEGORY", "SUB_CATEGORY", "STATUS"].includes(field.fieldKey)
                            ? dropdownHasMore[field.fieldKey] || false
                            : false
                      }
                      onSelect={(val) => handleFieldChange(field.fieldKey, val)}
                      accentColor={accent}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderTop: "1px solid #f1f5f9",
          gap: 1.5
        }}>
        {lensState === "analyzing" ? (
          <Button
            onClick={() => setLensState("idle")}
            variant="outlined"
            sx={{
              textTransform: "none",
              px: 3,
              py: 1,
              borderRadius: "9px",
              borderColor: "#e2e8f0",
              color: "#64748b",
              fontWeight: 500,
              "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" }
            }}>
            Cancel Analysis
          </Button>
        ) : lensState === "result" ? (
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
            <Button
              onClick={handleLensReject}
              disabled={!lensResult?.status}
              variant="outlined"
              color="error"
              sx={{
                textTransform: "none",
                px: 3,
                py: 1,
                borderRadius: "9px",
                fontWeight: 500,
                "&:disabled": {
                  color: "#cbd5e1",
                  borderColor: "#f1f5f9",
                  bgcolor: "#f8fafc"
                }
              }}>
              Reject
            </Button>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              {!lensResult?.status && (
                <Button
                  onClick={() => analyzeImages(lensSelectedFiles)}
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  sx={{
                    textTransform: "none",
                    px: 3,
                    py: 1,
                    borderRadius: "9px",
                    fontWeight: 600,
                    boxShadow: `0 4px 12px ${accent}44`,
                    bgcolor: accent,
                    "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
                  }}>
                  Retry
                </Button>
              )}
              <Button
                onClick={handleLensClick}
                variant="outlined"
                startIcon={<RefreshIcon />}
                sx={{
                  textTransform: "none",
                  px: 3,
                  py: 1,
                  borderRadius: "9px",
                  borderColor: "#e2e8f0",
                  color: "#64748b",
                  fontWeight: 500,
                  "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" }
                }}>
                Reupload
              </Button>
              {lensResult?.status && (
                <Button
                  onClick={handleLensAccept}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    px: 4,
                    py: 1,
                    borderRadius: "9px",
                    fontWeight: 600,
                    boxShadow: `0 4px 12px ${accent}44`,
                    bgcolor: accent,
                    "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
                  }}>
                  Accept
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1 }}>
              {visibleFields.filter((f) => f.isRequired).length > 0 && (
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  * Required fields
                </Typography>
              )}
            </Box>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: "none",
                px: 3,
                py: 1,
                borderRadius: "9px",
                borderColor: "#e2e8f0",
                color: "#64748b",
                fontWeight: 500,
                "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" }
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                textTransform: "none",
                px: 4,
                py: 1,
                borderRadius: "9px",
                fontWeight: 600,
                boxShadow: `0 4px 12px ${accent}44`,
                bgcolor: accent,
                "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
                "&:disabled": { opacity: 0.7 },
                minWidth: 130
              }}>
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} color="inherit" thickness={3} />
                  Saving…
                </Box>
              ) : (
                "Save Asset"
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddFixedAssetModal;
